import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { getTodayDateInputValue } from "../../utils/dateFormat";
import {
  Search,
  DollarSign,
  TrendingUp,
  Clock,
  Save,
  User,
  ArrowUpDown,
  Filter,
  CheckCircle,
  AlertCircle,
  ChevronRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE_URL } from "../../app/apiConfig";
import { getEmployeesApi, FILE_BASE_URL } from "../../app/api";

const INCREMENT_API_URL = `${API_BASE_URL}/increment`;

const normalizeFlag = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "active"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "inactive"].includes(normalized)) return false;
  return null;
};

const getNestedValue = (obj, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((current, part) => current?.[part], obj);
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
};

const getEmployeeName = (employee = {}) =>
  String(
    employee.name ||
    employee.employee_name ||
    employee.full_name ||
    `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
  ).trim();

const getIdentityValues = (employee = {}) => [
  employee.id,
  employee.user_id,
  employee.employee_id,
  employee.emp_id,
  employee.roll_number,
  employee.roll_no,
  employee.employee_code,
  employee.emp_code
].filter((value) => value !== undefined && value !== null && String(value).trim() !== "");

const isCurrentEmployee = (employee = {}) => {
  const status = String(getNestedValue(employee, [
    "employment_status",
    "employee_status",
    "user_status",
    "status",
    "user.status",
    "employee.status"
  ])).trim().toLowerCase();

  const activeFlag = normalizeFlag(getNestedValue(employee, [
    "is_active",
    "active",
    "isActive",
    "user.is_active",
    "employee.is_active"
  ]));

  const deletedFlag = normalizeFlag(getNestedValue(employee, [
    "is_deleted",
    "deleted",
    "isDeleted",
    "user.is_deleted",
    "employee.is_deleted"
  ]));

  const exitDate = getNestedValue(employee, [
    "deleted_at",
    "exit_date",
    "left_at",
    "leaving_date",
    "last_working_day",
    "resigned_at",
    "terminated_at",
    "relieved_at",
    "user.deleted_at",
    "employee.deleted_at"
  ]);

  const inactiveStatuses = ["inactive", "deleted", "ex", "ex employee", "ex-employee", "former", "left", "resigned", "terminated", "relieved", "archived"];

  if (activeFlag === false || deletedFlag === true || exitDate) return false;
  if (inactiveStatuses.includes(status)) return false;
  return true;
};

const buildEmployeeLookup = (users = []) => {
  const lookup = new Map();
  users.filter(isCurrentEmployee).forEach((user) => {
    getIdentityValues(user).forEach((value) => lookup.set(String(value), user));
    const name = getEmployeeName(user).toLowerCase();
    if (name) lookup.set(`name:${name}`, user);
  });
  return lookup;
};

const findRealEmployee = (incrementEmployee, lookup) => {
  for (const value of getIdentityValues(incrementEmployee)) {
    const match = lookup.get(String(value));
    if (match) return match;
  }

  const name = getEmployeeName(incrementEmployee).toLowerCase();
  return name ? lookup.get(`name:${name}`) : null;
};

const mergeWithRealEmployees = (incrementEmployees = [], users = []) => {
  const lookup = buildEmployeeLookup(users);
  return incrementEmployees.reduce((realRows, incrementEmployee) => {
    const matchedUser = findRealEmployee(incrementEmployee, lookup);
    if (!matchedUser) return realRows;

    realRows.push({
      ...matchedUser,
      ...incrementEmployee,
      name: incrementEmployee.name || getEmployeeName(matchedUser),
      profile_pic: incrementEmployee.profile_pic || matchedUser.profile_pic,
      user_id: incrementEmployee.user_id || matchedUser.id || matchedUser.user_id
    });
    return realRows;
  }, []);
};

const getProfileImageUrl = (employee = {}) => {
  const path = getNestedValue(employee, ["profile_pic", "profilePic", "profile_image", "user.profile_pic", "employee.profile_pic"]);
  if (!path) return "";
  const cleanPath = String(path).trim();
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
  return `${FILE_BASE_URL}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
};

const Increment = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const Tooltip = ({ text, children }) => {
    return (
      <div className="relative group flex items-center justify-center">
        {children}
        <div className="absolute bottom-full mb-2 px-2 py-1 text-[10px] font-medium text-white bg-black rounded-md whitespace-nowrap opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-100 pointer-events-none z-[100]">
          {text}
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchIncrementData();
  }, []);

  const fetchIncrementData = async () => {
    try {
      setLoading(true);
      const [res, usersRes] = await Promise.all([
        axios.get(`${INCREMENT_API_URL}/data`, { withCredentials: true }),
        getEmployeesApi()
      ]);
      if (res.data.success) {
        const incrementRows = res.data.data || [];
        if (usersRes.success) {
          setEmployees(mergeWithRealEmployees(incrementRows, usersRes.data || []));
        } else {
          setEmployees(incrementRows.filter(isCurrentEmployee));
        }
      }
    } catch (error) {
      console.error("Failed to fetch increment data", error);
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employee) => {
    setEditingId(employee.user_id);
    setEditValues({
      salary: employee.total_salary,
      hold_back_amount: employee.hold_back_amount,
      hold_back_period: employee.hold_back_period,
      hold_back_start_date: employee.hold_back_start_date || getTodayDateInputValue()
    });
  };

  const handleSave = async (userId) => {
    try {
      const res = await axios.post(
        `${INCREMENT_API_URL}/update`,
        {
          user_id: userId,
          ...editValues
        },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("Settings updated successfully");
        setEditingId(null);
        fetchIncrementData();
      }
    } catch (error) {
      console.error("Update failed", error);
      toast.error("Failed to update settings");
    }
  };

  const calculateNextMaturity = (startDate, period) => {
    if (!startDate || period <= 0) return "N/A";
    const start = new Date(startDate);
    const now = new Date();
    // Normalize dates to the first of the month for comparison
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    let maturity = new Date(start.getFullYear(), start.getMonth(), 1);
    // The maturity month is the (period)-th month.
    // If start is Month 1, then maturity is start + (period - 1).
    maturity.setMonth(maturity.getMonth() + (Number(period) - 1));

    // Keep adding the period until we find a maturity date that is either 
    // the current month or in the future.
    while (maturity < currentMonth) {
      maturity.setMonth(maturity.getMonth() + Number(period));
    }

    return maturity.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredEmployees = [...employees].sort((a, b) => (a.name || "").localeCompare(b.name || "")).filter((emp) =>
    (emp.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
              <TrendingUp className="text-purple-400" size={32} />
              INCREMENT <span className="text-white/40 font-light">& DEDUCTIONS</span>
            </h1>
            <p className="text-white/50 text-sm mt-1">Manage employee salaries and monthly hold-back rules</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-1.5 flex items-center gap-2">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent border-none pl-10 pr-4 py-2 text-sm focus:outline-none w-64"
              />
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Total Monthly Payroll"
            value={formatCurrency(employees.reduce((acc, curr) => acc + Number(curr.total_salary), 0))}
            icon={<DollarSign size={24} />}
            subtitle="Current payroll cost"
            color="purple"
          />
          <StatCard
            title="Monthly Hold-backs"
            value={formatCurrency(employees.reduce((acc, curr) => acc + Number(curr.hold_back_amount), 0))}
            icon={<Clock size={24} />}
            subtitle="Amount held this month"
            color="amber"
          />
          <StatCard
            title="Total Accumulated"
            value={formatCurrency(employees.reduce((acc, curr) => acc + Number(curr.accumulated_amount), 0))}
            icon={<CheckCircle size={24} />}
            subtitle="Lifetime deductions"
            color="green"
          />
        </div>

        {/* MAIN TABLE */}
        <div className="bg-[#12082f]/80 border border-white/10 rounded-[24px] overflow-hidden shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)] backdrop-blur-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40">Employee</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-right">Total Salary</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-right">Hold-back</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-right">After Deduction</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-center">Period</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-center">Start Date</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-center">Next Maturity</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-right">Accumulated</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-white/40 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                <AnimatePresence>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="p-20 text-center">
                        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto" />
                      </td>
                    </tr>
                  ) : currentEmployees.map((emp) => (
                    <motion.tr
                      key={emp.user_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="hover:bg-white/[0.03] transition-colors group"
                    >
                      <td className="p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold text-sm shadow-md border border-white/10 overflow-hidden">
                            {getProfileImageUrl(emp) ? (
                              <img
                                src={getProfileImageUrl(emp)}
                                alt={`${emp.name || "Employee"} profile`}
                                className="w-full h-full object-cover"
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                  const fallback = event.currentTarget.nextElementSibling;
                                  if (fallback) fallback.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <span className={`${getProfileImageUrl(emp) ? "hidden" : "flex"} w-full h-full items-center justify-center`}>
                              {(emp.name || "U")[0]}
                            </span>
                          </div>
                          <div>
                            <div className="font-bold text-sm text-white/90 group-hover:text-purple-300 transition-colors">{emp.name}</div>
                            <div className="text-[9px] text-white/40 uppercase font-bold tracking-widest">ID: {emp.user_id}</div>
                          </div>
                        </div>
                      </td>

                      <td className="p-5 text-right">
                        {editingId === emp.user_id ? (
                          <input
                            type="number"
                            value={editValues.salary}
                            onChange={(e) => setEditValues({ ...editValues, salary: e.target.value })}
                            className="bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-right w-28 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-white"
                          />
                        ) : (
                          <div className="font-semibold text-[15px] text-white/80">{formatCurrency(emp.total_salary)}</div>
                        )}
                      </td>

                      <td className="p-5 text-right">
                        {editingId === emp.user_id ? (
                          <input
                            type="number"
                            value={editValues.hold_back_amount}
                            onChange={(e) => setEditValues({ ...editValues, hold_back_amount: e.target.value })}
                            className="bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-right w-24 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all text-white"
                          />
                        ) : (
                          <div className="text-red-400/80 font-medium text-[14px]">-{formatCurrency(emp.hold_back_amount)}</div>
                        )}
                      </td>

                      <td className="p-5 text-right">
                        <div className="font-black text-green-400 text-[15px]">
                          {formatCurrency(editingId === emp.user_id ? (editValues.salary - editValues.hold_back_amount) : emp.salary_after_deduction)}
                        </div>
                      </td>

                      <td className="p-5 text-center">
                        {editingId === emp.user_id ? (
                          <select
                            value={editValues.hold_back_period}
                            onChange={(e) => setEditValues({ ...editValues, hold_back_period: e.target.value })}
                            className="bg-[#12082f] border border-white/20 rounded-lg px-2 py-1.5 text-[12px] focus:border-purple-500 outline-none transition-all text-white/80"
                          >
                            <option value="0">No Rule</option>
                            <option value="3">3 Months</option>
                            <option value="6">6 Months</option>
                            <option value="12">1 Year</option>
                          </select>
                        ) : (
                          <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${emp.hold_back_period > 0 ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-white/5 text-white/30 border border-white/10'}`}>
                            {emp.hold_back_period > 0 ? `${emp.hold_back_period} Months` : 'No Rule'}
                          </span>
                        )}
                      </td>

                      <td className="p-5 text-center">
                        {editingId === emp.user_id ? (
                          <input
                            type="date"
                            value={editValues.hold_back_start_date}
                            onChange={(e) => setEditValues({ ...editValues, hold_back_start_date: e.target.value })}
                            className="bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-[12px] focus:border-purple-500 outline-none transition-all text-white/80 [color-scheme:dark]"
                          />
                        ) : (
                          <div className="text-white/50 text-[13px] font-medium">
                            {emp.hold_back_start_date ? new Date(emp.hold_back_start_date).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                          </div>
                        )}
                      </td>

                      <td className="p-5 text-center">
                        <div className="flex items-center justify-center gap-1.5 text-white/70">
                          <Clock size={13} className="text-purple-400/80" />
                          <span className="font-semibold text-[13px]">
                            {calculateNextMaturity(emp.hold_back_start_date, emp.hold_back_period)}
                          </span>
                        </div>
                      </td>

                      <td className="p-5 text-right">
                        <div className="flex items-center justify-end gap-1.5 text-white/70">
                          <Clock size={13} className="text-amber-500/80" />
                          <span className="font-mono text-[14px]">{formatCurrency(emp.accumulated_amount)}</span>
                        </div>
                      </td>

                      <td className="p-5 text-center">
                        {editingId === emp.user_id ? (
                          <div className="flex items-center justify-center gap-2">
                            <Tooltip text="Save Details">
                              <button
                                onClick={() => handleSave(emp.user_id)}
                                className="p-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 hover:scale-105 transition-all"
                              >
                                <Save size={16} />
                              </button>
                            </Tooltip>
                            {/* <button
                              onClick={() => setEditingId(null)}
                              className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 hover:scale-105 transition-all"
                            >
                              <AlertCircle size={16} />
                            </button> */}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEdit(emp)}
                            className="bg-white/5 hover:bg-purple-500/20 hover:text-purple-400 border border-white/5 hover:border-purple-500/30 p-1.5 rounded-lg transition-all hover:scale-105 text-white/40"
                          >
                            <Filter size={16} />
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {filteredEmployees.length > itemsPerPage && (
            <div className="flex items-center justify-center gap-2 py-6 border-t border-white/5 bg-white/[0.01]">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
              >
                <ChevronRight size={18} className="rotate-180" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}

          {!loading && filteredEmployees.length === 0 && (
            <div className="p-16 text-center">
              <User size={40} className="mx-auto text-white/10 mb-3" />
              <div className="text-base font-bold text-white/40">No employees found</div>
              <p className="text-white/20 text-xs">Try searching with a different name</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, subtitle, color }) => {
  const colors = {
    green: "from-emerald-500/10 to-[#12082f]/80 text-emerald-400 border-emerald-500/20 icon-bg-emerald-500/10",
    blue: "from-blue-500/10 to-[#12082f]/80 text-blue-400 border-blue-500/20 icon-bg-blue-500/10",
    purple: "from-purple-500/10 to-[#12082f]/80 text-purple-400 border-purple-500/20 icon-bg-purple-500/10",
    amber: "from-amber-500/10 to-[#12082f]/80 text-amber-400 border-amber-500/20 icon-bg-amber-500/10",
  };

  const currentStyle = colors[color] || "from-white/5 to-transparent text-white border-white/10 icon-bg-white/5";
  const [gradientStart, , gradientEnd, textColor, borderColor, iconBg] = currentStyle.split(" ");

  return (
    <div className={`bg-gradient-to-br ${gradientStart} via-transparent ${gradientEnd} border ${borderColor} p-6 rounded-[24px] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden backdrop-blur-xl`}>
      {/* Top right diagonal slash */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-white/[0.02] -rotate-45 translate-x-20 -translate-y-20 group-hover:translate-x-16 transition-transform duration-500 pointer-events-none" />

      <div className="flex items-center justify-between mb-6 relative z-10">
        <div className={`p-0 bg-transparent ${textColor} group-hover:scale-110 transition duration-300`}>
          {icon}
        </div>
        <div className="text-[11px] font-black text-white/80 uppercase tracking-widest">{title}</div>
      </div>

      <div className="relative z-10 mt-2">
        <div className={`text-4xl font-black ${textColor} drop-shadow-md`}>{value}</div>
        <div className="text-xs text-white/50 mt-2 font-medium tracking-wide">{subtitle}</div>
      </div>
    </div>
  );
};

export default Increment;
